import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Mail,
  Phone,
  Calendar,
  Edit2,
  Lock,
  Eye,
  EyeOff,
  Check,
  X,
  UserCircle,
  CreditCard,
} from "lucide-react";
import { toast } from "react-toastify";
import { getPaymentHistory } from '../apis/payment';
import SubscriptionCard from "../components/SubscriptionCard";
import 'jspdf-autotable';

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: "", newPassword: "" });
  const [passwordError, setPasswordError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);

  const getSubscriptionHistory = async () => {
    try {
      setSubscriptionsLoading(true);
      const response = await getPaymentHistory();
      if (response && response.data) {
        // Sort by created_at descending (newest first)
        const sortedSubscriptions = response.data.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setSubscriptions(sortedSubscriptions);
      }
    } catch (error) {
      console.error("Error fetching subscription history:", error);
      toast.error("Failed to load subscription history");
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  useEffect(() => {
    const user = localStorage.getItem("user");
    getSubscriptionHistory();
    if (user) {
      const parsed = JSON.parse(user);
      setUserData(parsed);
      setForm({
        name: parsed.name || "",
        email: parsed.email || "",
        phone: parsed.phone || "",
      });
    }
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
    if (passwordError) setPasswordError("");
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("userAccessToken");
    localStorage.removeItem("user_id");
    window.location.href = "/login";
  };

  const updateUserDetails = async () => {
    setStatusMessage("");
    setIsLoading(true);
    try {
      const token = await localStorage.getItem("userAccessToken");

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/updateUser`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(form),
        }
      );

      const data = await res.json();

      if (!data.success) throw new Error(data.error || "Failed to update");

      setUserData(data.user);
      localStorage.setItem("user", JSON.stringify(data.user));
      setIsEditing(false);
      setStatusMessage("Profile updated successfully.");
      toast.success("Profile updated successfully!");

      // Refresh the whole website after 1.5 seconds
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      setStatusMessage(err.message);
      toast.error(err.message || "Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async () => {
    setPasswordError("");
    setIsPasswordLoading(true);
    try {
      const token = localStorage.getItem("userAccessToken");

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/updatePassword`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(passwordForm),
        }
      );

      const data = await res.json();

      if (!data.success)
        throw new Error(data.error || "Failed to change password");

      setShowPasswordModal(false);
      setPasswordForm({ oldPassword: "", newPassword: "" });

      // Show success toast and logout user
      toast.success(
        "Password changed successfully! You will be logged out for security."
      );
      setTimeout(() => {
        handleLogout();
      }, 2000);
    } catch (err) {
      setPasswordError(err.message);
      toast.error(err.message || "Failed to change password");
    } finally {
      setIsPasswordLoading(false);
    }
  };

  if (!userData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-3 border-[#01CC54]/40 border-t-[#01CC54] rounded-full"
        />
      </div>
    );
  }

return (
  <div className="min-h-screen bg-white text-gray-900 py-3 px-3 sm:px-4 lg:px-5">
    {/* Desktop Layout */}
    <div className="hidden lg:block">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mx-auto"
      >
        {/* Header Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg shadow-lg p-3 mb-4 border border-gray-200"
        >
          <div className="flex items-center gap-2.5">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="w-10 h-10 rounded-full flex items-center justify-center shadow ring-1 ring-gray-200 bg-gray-50"
            >
              <UserCircle className="w-5 h-5 text-[#01CC54]" />
            </motion.div>
            <div>
              <h1 className="text-lg font-bold mb-0.5 text-gray-900">{userData.name}</h1>
              <p className="text-xs text-[#01CC54] font-medium tracking-wide">
                Profile Settings
              </p>
            </div>
          </div>
        </motion.div>

        {/* Status Message */}
        <AnimatePresence>
          {statusMessage && (
            <motion.div
              initial={{ opacity: 0, y: -16, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -16, scale: 0.95 }}
              className={`mb-3 p-2.5 rounded-lg text-sm ${
                statusMessage.includes("successfully")
                  ? "bg-emerald-50 border border-emerald-300 text-emerald-700"
                  : "bg-rose-50 border border-rose-300 text-rose-700"
              }`}
            >
              <div className="flex items-center gap-1.5">
                {statusMessage.includes("successfully") ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <X className="w-4 h-4" />
                )}
                <span className="text-xs">{statusMessage}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content Grid */}
        <div className="grid grid-cols-12 gap-3">
          {/* Profile Information - Left Side */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="col-span-5"
          >
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden h-fit">
              <div className="p-3 border-b border-gray-200 bg-white">
                <h2 className="text-sm font-bold text-gray-900">Personal Information</h2>
              </div>
              <div className="p-3">
                <AnimatePresence mode="wait">
                  {isEditing ? (
                    <motion.div
                      key="editing"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-3"
                    >
                      {/* Name */}
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                          <User className="w-3.5 h-3.5 text-[#01CC54]" />
                          Full Name
                        </label>
                        <input
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          type="text"
                          className="w-full px-3 py-2 text-sm rounded border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20"
                          placeholder="Enter your full name"
                        />
                      </div>
                      {/* Email */}
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                          <Mail className="w-3.5 h-3.5 text-[#01CC54]" />
                          Email
                        </label>
                        <input
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          type="email"
                          className="w-full px-3 py-2 text-sm rounded border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20"
                          placeholder="Enter your email"
                        />
                      </div>
                      {/* Phone */}
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                          <Phone className="w-3.5 h-3.5 text-[#01CC54]" />
                          Phone
                        </label>
                        <input
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          type="text"
                          className="w-full px-3 py-2 text-sm rounded border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20"
                          placeholder="Enter your phone number"
                        />
                      </div>
                      {/* Actions */}
                      <div className="flex gap-2 pt-1.5">
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={updateUserDetails}
                          disabled={isLoading}
                          className="flex-1 bg-[#048AAD] text-white py-2 text-xs rounded-lg font-semibold shadow hover:bg-[#048AAD]/90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                        >
                          {isLoading ? (
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{
                                duration: 1,
                                repeat: Infinity,
                                ease: "linear",
                              }}
                              className="w-4 h-4 border-2 border-emerald-200/40 border-t-emerald-400 rounded-full"
                            />
                          ) : (
                            <>
                              <Check className="w-4 h-4" />
                              <span>Save</span>
                            </>
                          )}
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => {
                            setIsEditing(false);
                            setForm({
                              name: userData.name,
                              email: userData.email,
                              phone: userData.phone,
                            });
                            setStatusMessage("");
                          }}
                          className="flex-1 bg-gray-100 text-gray-700 py-2 text-xs rounded-lg font-semibold border border-gray-200 hover:bg-gray-200 transition flex items-center justify-center gap-1.5"
                        >
                          <X className="w-4 h-4" />
                          Cancel
                        </motion.button>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="viewing"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-2.5"
                    >
                      {/* Info Items */}
                      {[
                        { Icon: User, label: "Full Name", value: userData.name },
                        { Icon: Mail, label: "Email", value: userData.email },
                        { Icon: Phone, label: "Phone", value: userData.phone },
                        {
                          Icon: Calendar,
                          label: "Member Since",
                          value: new Date(userData.created_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }),
                        },
                      ].map(({ Icon, label, value }) => (
                        <motion.div
                          key={label}
                          whileHover={{ scale: 1.02 }}
                          className="flex items-center gap-2.5 p-2.5 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <Icon className="w-4 h-4 text-[#01CC54] flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-600">{label}</p>
                            <p className="text-sm font-semibold text-gray-900 truncate">{value}</p>
                          </div>
                        </motion.div>
                      ))}
                      {/* Actions */}
                      <div className="flex gap-2 pt-2.5">
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => setIsEditing(true)}
                          className="flex-1 bg-[#048AAD] text-white py-2 text-xs rounded-lg font-semibold shadow hover:bg-[#048AAD]/90 transition flex items-center justify-center gap-1.5"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit Profile
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => setShowPasswordModal(true)}
                          className="flex-1 bg-gray-100 text-gray-700 py-2 text-xs rounded-lg font-semibold border border-gray-200 hover:bg-gray-200 transition flex items-center justify-center gap-1.5"
                        >
                          <Lock className="w-4 h-4" />
                          Change Password
                        </motion.button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* Subscription History - Right Side */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="col-span-7"
          >
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-3 border-b border-gray-200 bg-white">
                <h2 className="text-sm font-bold flex items-center gap-1.5 text-gray-900">
                  <CreditCard className="w-4 h-4 text-[#01CC54]" />
                  Subscription History
                </h2>
              </div>

              <div className="p-3">
                {subscriptionsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-8 h-8 border-3 border-emerald-200/40 border-t-emerald-400 rounded-full"
                    />
                  </div>
                ) : subscriptions.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto pr-2 scrollbar-hide">
                    {subscriptions.map((subscription, index) => (
                      <SubscriptionCard
                        key={subscription.id}
                        subscription={subscription}
                        index={index}
                      />
                    ))}
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-16 h-16 bg-gray-50 rounded-full ring-1 ring-gray-200 flex items-center justify-center mx-auto mb-3">
                      <CreditCard className="w-8 h-8 text-gray-600" />
                    </div>
                    <h3 className="text-base font-semibold mb-1.5 text-gray-900">No Subscriptions Found</h3>
                    <p className="text-sm text-gray-600">
                      You haven't purchased any subscription plans yet.
                    </p>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>

    {/* Mobile Layout */}
    <div className="lg:hidden">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md mx-auto"
      >
        {/* Header Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg shadow-lg p-3 mb-3 border border-gray-200"
        >
          <div className="flex items-center gap-2.5">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="w-10 h-10 rounded-full flex items-center justify-center shadow ring-1 ring-gray-200 bg-gray-50"
            >
              <UserCircle className="w-5 h-5 text-[#01CC54]" />
            </motion.div>
            <div className="truncate">
              <h1 className="text-base font-bold mb-0.5 truncate text-gray-900">
                {userData.name}
              </h1>
              <p className="text-xs text-[#01CC54] font-medium tracking-wide">
                Profile Settings
              </p>
            </div>
          </div>
        </motion.div>

        {/* Status Message */}
        <AnimatePresence>
          {statusMessage && (
            <motion.div
              initial={{ opacity: 0, y: -16, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -16, scale: 0.95 }}
              className={`mb-2.5 p-2 rounded-lg text-xs ${
                statusMessage.includes("successfully")
                  ? "bg-emerald-50 border border-emerald-300 text-emerald-700"
                  : "bg-rose-50 border border-rose-300 text-rose-700"
              }`}
            >
              <div className="flex items-center gap-1.5">
                {statusMessage.includes("successfully") ? (
                  <Check className="w-3.5 h-3.5" />
                ) : (
                  <X className="w-3.5 h-3.5" />
                )}
                <span className="text-xs">{statusMessage}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden mb-3"
        >
          <div className="p-2.5 border-b border-gray-200 bg-white">
            <h2 className="text-sm font-bold text-gray-900">Personal Information</h2>
          </div>
          <div className="p-3">
            <AnimatePresence mode="wait">
              {isEditing ? (
                <motion.div
                  key="editing"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-3"
                >
                  {/* Name */}
                  <div className="space-y-1">
                    <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                      <User className="w-3.5 h-3.5 text-[#01CC54]" />
                      Full Name
                    </label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      type="text"
                      className="w-full px-2.5 py-1.5 text-xs rounded border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20"
                      placeholder="Enter your full name"
                    />
                  </div>
                  {/* Email */}
                  <div className="space-y-1">
                    <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                      <Mail className="w-3.5 h-3.5 text-[#01CC54]" />
                      Email
                    </label>
                    <input
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      type="email"
                      className="w-full px-2.5 py-1.5 text-xs rounded border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20"
                      placeholder="Enter your email"
                    />
                  </div>
                  {/* Phone */}
                  <div className="space-y-1">
                    <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                      <Phone className="w-3.5 h-3.5 text-[#01CC54]" />
                      Phone
                    </label>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      type="text"
                      className="w-full px-2.5 py-1.5 text-xs rounded border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-1.5 pt-1.5">
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={updateUserDetails}
                      disabled={isLoading}
                      className="flex-1 bg-[#048AAD] text-white py-1.5 text-xs rounded-lg font-semibold shadow hover:bg-[#048AAD]/90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                    >
                      {isLoading ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                          className="w-3.5 h-3.5 border-2 border-[#01CC54]/40 border-t-[#01CC54] rounded-full"
                        />
                      ) : (
                        <>
                          <Check className="w-3.5 h-3.5" />
                          <span>Save</span>
                        </>
                      )}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => {
                        setIsEditing(false);
                        setForm({
                          name: userData.name,
                          email: userData.email,
                          phone: userData.phone,
                        });
                        setStatusMessage("");
                      }}
                      className="flex-1 bg-gray-100 text-gray-700 py-1.5 text-xs rounded-lg font-semibold border border-gray-200 hover:bg-gray-200 transition flex items-center justify-center gap-1"
                    >
                      <X className="w-3.5 h-3.5" />
                      Cancel
                    </motion.button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="viewing"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-2.5"
                    >
                      {/* Info Items */}
                      {[
                        { Icon: User, label: "Full Name", value: userData.name },
                        { Icon: Mail, label: "Email", value: userData.email },
                        { Icon: Phone, label: "Phone", value: userData.phone },
                        {
                          Icon: Calendar,
                          label: "Member Since",
                          value: new Date(userData.created_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }),
                        },
                      ].map(({ Icon, label, value }) => (
                        <motion.div
                          key={label}
                          whileHover={{ scale: 1.01 }}
                          className="flex items-center gap-2.5 p-2.5 bg-gray-50 rounded border border-gray-200"
                        >
                          <Icon className="w-3.5 h-3.5 text-[#01CC54] flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-medium text-gray-600">{label}</p>
                            <p className="text-xs font-semibold text-gray-900 truncate">{value}</p>
                          </div>
                        </motion.div>
                      ))}
                      {/* Actions */}
                      <div className="flex flex-col sm:flex-row gap-1.5 pt-2">
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => setIsEditing(true)}
                          className="flex-1 bg-[#048AAD] text-white py-1.5 text-xs rounded-lg font-semibold shadow hover:bg-[#048AAD]/90 transition flex items-center justify-center gap-1"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                          Edit
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => setShowPasswordModal(true)}
                          className="flex-1 bg-gray-100 text-gray-700 py-1.5 text-xs rounded-lg font-semibold border border-gray-200 hover:bg-gray-200 transition flex items-center justify-center gap-1"
                        >
                          <Lock className="w-3.5 h-3.5" />
                          Change Password
                        </motion.button>
                      </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Subscription History Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="p-2.5 border-b border-gray-200 bg-white">
              <h2 className="text-sm font-bold flex items-center gap-1.5 text-gray-900">
                <CreditCard className="w-4 h-4 text-[#01CC54]" />
                Subscription History
              </h2>
            </div>

            <div className="p-3">
              {subscriptionsLoading ? (
                <div className="flex items-center justify-center py-6">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-6 h-6 border-2 border-emerald-200/40 border-t-emerald-400 rounded-full"
                  />
                </div>
              ) : subscriptions.length > 0 ? (
                <div className="space-y-3">
                  {subscriptions.map((subscription, index) => (
                    <SubscriptionCard
                      key={subscription.id}
                      subscription={subscription}
                      index={index}
                    />
                  ))}
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                    className="text-center py-6"
                >
                  <div className="w-12 h-12 bg-gray-50 rounded-full ring-1 ring-gray-200 flex items-center justify-center mx-auto mb-3">
                    <CreditCard className="w-6 h-6 text-gray-600" />
                  </div>
                  <h3 className="text-sm font-semibold mb-1.5 text-gray-900">No Subscriptions Found</h3>
                  <p className="text-xs text-gray-600">
                    You haven't purchased any subscription plans yet.
                  </p>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>

    {/* Password Modal */}
    <AnimatePresence>
      {showPasswordModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-white rounded-lg shadow-2xl w-full max-w-sm overflow-hidden border border-gray-200"
          >
            <div className="p-2.5 border-b border-gray-200 bg-white">
              <h3 className="text-sm font-bold flex items-center gap-1.5 text-gray-900">
                <Lock className="w-3.5 h-3.5 text-[#01CC54]" />
                Change Password
              </h3>
            </div>
            <div className="p-3 space-y-3 bg-white">
              {/* Old Password */}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-600">Current Password</label>
                <div className="relative">
                  <input
                    type={showOldPassword ? "text" : "password"}
                    name="oldPassword"
                    value={passwordForm.oldPassword}
                    onChange={handlePasswordChange}
                    className="w-full px-2.5 py-1.5 pr-8 text-xs rounded border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowOldPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-[#01CC54]"
                  >
                    {showOldPassword ? (
                      <EyeOff className="w-3.5 h-3.5" />
                    ) : (
                      <Eye className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              </div>
              {/* New Password */}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-600">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    className="w-full px-2.5 py-1.5 pr-8 text-xs rounded border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-[#01CC54]"
                  >
                    {showNewPassword ? (
                      <EyeOff className="w-3.5 h-3.5" />
                    ) : (
                      <Eye className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              </div>
              {/* Error Message */}
              <AnimatePresence>
                {passwordError && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-rose-50 border border-rose-300 text-rose-700 p-1.5 rounded flex items-center gap-1.5 text-xs"
                  >
                    <X className="w-3.5 h-3.5" />
                    <span className="text-xs">{passwordError}</span>
                  </motion.div>
                )}
              </AnimatePresence>
              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-1.5 pt-1.5">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={changePassword}
                  disabled={isPasswordLoading}
                  className="flex-1 bg-[#048AAD] text-white py-1.5 text-xs rounded font-semibold shadow hover:bg-[#048AAD]/90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                >
                  {isPasswordLoading ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-3.5 h-3.5 border-2 border-[#01CC54]/40 border-t-[#01CC54] rounded-full"
                    />
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Update
                    </>
                  )}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({ oldPassword: "", newPassword: "" });
                    setPasswordError("");
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 py-1.5 text-xs rounded font-semibold hover:bg-gray-200 border border-gray-200 transition flex items-center justify-center gap-1"
                >
                  <X className="w-3.5 h-3.5" />
                  Cancel
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

};

export default Profile;
